/**
 * BreathOrb — the canonical Reclaim breath orb.
 *
 * Soft, gold, radial. Never a hard solid disc. Used by every breath /
 * arrival / reset surface across the app so the visual language is
 * consistent.
 *
 * Props
 *   size       (number)   Diameter in px of the core glow. Default 156.
 *   phase      (string)   Optional — "inhale" | "hold" | "exhale" | "rest"
 *                         Drives a smooth scale ramp. Omit for free pulse.
 *   pulseMs    (number)   Cycle duration when phase is not provided.
 *   tone       ("gold" | "white" | "cyan")  Default "gold".
 *   muted      (bool)     Halves intensity for ambient backgrounds.
 *
 * Layers (all radial gradients, never opaque):
 *   1. Outer halo  — wide soft glow, breathes scale/opacity
 *   2. Middle ring — gentle hover ring, follows the breath
 *   3. Core seed   — small radiant centre, never solid
 */
import { motion } from "framer-motion";
import { useMemo } from "react";

const TONES = {
  gold: {
    coreInner: "#FFFFFF",
    coreMid:   "#FFE9A8",
    coreOuter: "rgba(255, 233, 168, 0)",
    halo:      "rgba(215, 187, 122, 0.55)",
    ring:      "rgba(215, 187, 122, 0.35)",
    shadow:    "rgba(215, 187, 122, 0.55)",
  },
  white: {
    coreInner: "#FFFFFF",
    coreMid:   "#F2EAD3",
    coreOuter: "rgba(242, 234, 211, 0)",
    halo:      "rgba(255, 255, 255, 0.32)",
    ring:      "rgba(255, 255, 255, 0.22)",
    shadow:    "rgba(255, 255, 255, 0.45)",
  },
  cyan: {
    coreInner: "#FFFFFF",
    coreMid:   "#C4DEFF",
    coreOuter: "rgba(196, 222, 255, 0)",
    halo:      "rgba(77, 163, 255, 0.45)",
    ring:      "rgba(196, 222, 255, 0.28)",
    shadow:    "rgba(77, 163, 255, 0.45)",
  },
};

const PHASE_SCALE = {
  inhale: 1.22,
  hold:   1.22,
  exhale: 0.94,
  rest:   1.0,
};

export default function BreathOrb({
  size = 156,
  phase,
  pulseMs = 5500,
  tone = "gold",
  muted = false,
  className = "",
  "data-testid": testId = "breath-orb",
}) {
  const palette = TONES[tone] || TONES.gold;
  const dim = muted ? 0.6 : 1;

  // Phase-driven animation (controlled). Falls back to a soft free pulse
  // when no phase is provided so it can stand alone.
  const coreAnim = useMemo(() => {
    if (phase) {
      return {
        scale: PHASE_SCALE[phase] ?? 1,
        opacity: phase === "exhale" || phase === "rest" ? 0.78 : 1,
      };
    }
    return {
      scale: [1, 1.14, 1],
      opacity: [0.82, 1, 0.82],
    };
  }, [phase]);

  const coreTransition = phase
    ? { duration: 2.4, ease: "easeInOut" }
    : { duration: pulseMs / 1000, repeat: Infinity, ease: "easeInOut" };

  const haloAnim = phase
    ? {
        scale: (PHASE_SCALE[phase] ?? 1) * 1.05,
        opacity: phase === "exhale" || phase === "rest" ? 0.45 : 0.75,
      }
    : { scale: [1, 1.20, 1], opacity: [0.55, 0.85, 0.55] };

  const haloTransition = phase
    ? { duration: 2.4, ease: "easeInOut" }
    : { duration: pulseMs / 1000, repeat: Infinity, ease: "easeInOut" };

  const coreSize = Math.round(size * 0.22);
  const ringSize = Math.round(size * 0.52);

  return (
    <div
      className={`relative inline-flex items-center justify-center pointer-events-none ${className}`}
      style={{ width: size, height: size }}
      aria-hidden
      data-testid={testId}
    >
      {/* Outer halo — wide diffuse glow, never opaque */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: size,
          height: size,
          background: `radial-gradient(circle at 50% 50%, ${palette.halo} 0%, ${palette.coreOuter} 70%)`,
          opacity: dim,
        }}
        animate={haloAnim}
        transition={haloTransition}
      />

      {/* Middle hover ring — outline only, no fill */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: ringSize,
          height: ringSize,
          border: `1px solid ${palette.ring}`,
          boxShadow: `0 0 22px ${palette.shadow}33`,
          opacity: dim,
        }}
        animate={
          phase
            ? { scale: (PHASE_SCALE[phase] ?? 1) * 1.02 }
            : { scale: [1, 1.10, 1], opacity: [0.5, 0.75, 0.5] }
        }
        transition={coreTransition}
      />

      {/* Core seed — radiant, fades to transparent */}
      <motion.div
        className="rounded-full"
        style={{
          width: coreSize,
          height: coreSize,
          background: `radial-gradient(circle at 50% 40%, ${palette.coreInner} 0%, ${palette.coreMid} 55%, ${palette.coreOuter} 100%)`,
          boxShadow: `0 0 32px ${palette.shadow}, 0 0 70px ${palette.shadow}88`,
          opacity: dim,
        }}
        animate={coreAnim}
        transition={coreTransition}
      />
    </div>
  );
}
